import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { FiAlertCircle, FiAlertTriangle, FiX } from "react-icons/fi";
import {
  DeleteFolderDocument,
  type DeleteFolderMutation,
  type DeleteFolderMutationVariables,
  GetFoldersDocument,
  GetMediaDocument,
  GetTrashedMediaDocument,
  PermanentlyDeleteDocument,
  type PermanentlyDeleteMutation,
  type PermanentlyDeleteMutationVariables,
} from "../../../generated/graphql";

import type { MediaType } from "../../../generated/graphql";

/**
 * ConfirmDeleteModal - Permanently deletes items (cannot be undone)
 *
 * IMPORTANT: For media items, this should only be used for items that are already in trash.
 * Regular deletion should use MoveToTrash mutation instead (trash-first workflow).
 *
 * For folders, this performs immediate permanent deletion.
 */
interface ConfirmDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "folder" | "media";
  itemIds: string[]; // Changed to array for bulk delete
  itemName: string; // Display name (e.g., "photo.jpg" or "3 items")
  // For cache updates
  parentId?: string | null; // Required for folder deletion to update cache
  mediaType?: MediaType; // Required for folder/media deletion to update cache
}

export function ConfirmDeleteModal({
  isOpen,
  onClose,
  type,
  itemIds,
  itemName,
  parentId,
  mediaType,
}: ConfirmDeleteModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [deletingCount, setDeletingCount] = useState(0);

  const [deleteFolder, { loading: deletingFolder }] = useMutation<
    DeleteFolderMutation,
    DeleteFolderMutationVariables
  >(DeleteFolderDocument, {
    onCompleted: () => {
      setError(null);
      // Only close after all deletions complete (handled in handleDelete)
    },
    onError: (err) => {
      setError(err.message);
    },
    update: (cache, _result, options) => {
      const deletedId = options?.variables?.id;
      if (!deletedId) return;

      // Remove from cache
      const cacheId = cache.identify({ __typename: "Folder", id: deletedId });
      if (cacheId) {
        cache.evict({ id: cacheId });
      }
      cache.gc();

      // Update folders list if we have the parent info
      if (parentId !== undefined && mediaType) {
        const existingFolders = cache.readQuery({
          query: GetFoldersDocument,
          variables: { parentId, type: mediaType },
        });

        if (existingFolders?.folders) {
          cache.writeQuery({
            query: GetFoldersDocument,
            variables: { parentId, type: mediaType },
            data: {
              folders: {
                ...existingFolders.folders,
                edges: existingFolders.folders.edges.filter((e) => e.node.id !== deletedId),
              },
            },
          });
        }
      }
    },
  });

  const [permanentlyDeleteMedia, { loading: deletingMedia }] = useMutation<
    PermanentlyDeleteMutation,
    PermanentlyDeleteMutationVariables
  >(PermanentlyDeleteDocument, {
    onCompleted: () => {
      setError(null);
      // Only close after all deletions complete (handled in handleDelete)
    },
    onError: (err) => {
      setError(err.message);
    },
    update: (cache, _result, options) => {
      const deletedId = options?.variables?.id;
      if (!deletedId) return;

      // Remove from cache - try all possible types
      const typeNames = ["Image", "Video", "Audio", "File"] as const;
      for (const typeName of typeNames) {
        const cacheId = cache.identify({
          __typename: typeName,
          id: deletedId,
        });
        if (cacheId) {
          cache.evict({ id: cacheId });
        }
      }
      cache.gc();

      // Update trashed media list if we have the media type
      if (mediaType) {
        const existingTrashedMedia = cache.readQuery({
          query: GetTrashedMediaDocument,
          variables: { type: mediaType },
        });

        if (existingTrashedMedia?.trashedMedia) {
          cache.writeQuery({
            query: GetTrashedMediaDocument,
            variables: { type: mediaType },
            data: {
              trashedMedia: {
                ...existingTrashedMedia.trashedMedia,
                edges: existingTrashedMedia.trashedMedia.edges.filter(
                  (e) => e.node.id !== deletedId
                ),
              },
            },
          });
        }
      }
    },
  });

  const loading = deletingFolder || deletingMedia;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !loading) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, loading, onClose]);

  const handleDelete = async () => {
    setError(null);
    setDeletingCount(0);

    try {
      for (let i = 0; i < itemIds.length; i++) {
        const currentId = itemIds[i];
        if (!currentId) continue;

        setDeletingCount(i + 1);

        if (type === "folder") {
          await deleteFolder({
            variables: { id: currentId },
          });
        } else {
          await permanentlyDeleteMedia({
            variables: { id: currentId },
          });
        }
      }
      // All deletions successful
      onClose();
    } catch {
      // Error already handled by onError callback
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={loading ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <FiAlertTriangle className="h-5 w-5 text-red-600" />
            <h2 id="confirm-delete-title" className="text-lg font-semibold text-gray-900">
              Confirm Deletion
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            disabled={loading}
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Warning */}
          <div className="mb-4 rounded-md bg-yellow-50 p-4">
            <div className="flex items-start gap-3">
              <FiAlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600" />
              <div className="text-sm text-yellow-800">
                {type === "folder" ? (
                  <>
                    <p className="font-medium">
                      Are you sure you want to delete the folder "{itemName}"?
                    </p>
                    <p className="mt-1">
                      This will permanently delete the folder and <strong>all its contents</strong>,
                      including subfolders and files. This action cannot be undone.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">
                      Are you sure you want to delete{" "}
                      {itemIds.length === 1 ? `"${itemName}"` : itemName}?
                    </p>
                    <p className="mt-1">
                      This will permanently delete{" "}
                      {itemIds.length === 1 ? "the file" : "these files"} from storage. This action
                      cannot be undone.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-800">
              <FiAlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading
                ? itemIds.length > 1
                  ? `Deleting ${deletingCount} of ${itemIds.length}...`
                  : "Deleting..."
                : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
